import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Factory, ArrowRight, ArrowLeft, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Category() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/public/category/${slug}`);
      setData(r.data);
      document.title = `${r.data.name} — LumberLoop USA`;
    } catch (e) {
      console.error("Failed to load category:", e);
      setError(e?.response?.status === 404 ? "Category not found" : "Failed to load category");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchCategory();
    window.scrollTo(0, 0);
  }, [fetchCategory]);

  if (loading) {
    return <div className="py-32 text-center font-mono-ll text-sm tracking-widest text-slate-500">LOADING…</div>;
  }
  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-heading text-4xl font-black uppercase text-[#1A365D]">{error}</h1>
        <Link to="/" className="btn-primary mt-6 inline-flex">← Back home</Link>
      </div>
    );
  }

  const mfrs = data.manufacturers || [];

  return (
    <div data-testid="category-page">
      {/* HERO */}
      <section className="relative bg-[#1A365D] text-white overflow-hidden border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <Link to="/" className="font-mono-ll text-[11px] tracking-[0.3em] text-slate-300 hover:text-[#C62828] inline-flex items-center gap-2" data-testid="back-home-link">
            <ArrowLeft size={14}/> All Categories
          </Link>
          <div className="label-tag text-slate-300 mt-4">Brands & Products</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase mt-2 leading-none">
            {data.name}
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">
            Browse manufacturers and products in this category. Pick a specific brand or request a quote from all matching dealers in your market.
          </p>
        </div>
        <div className="h-2 stripes-flag"></div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div className="font-mono-ll text-xs tracking-widest text-slate-500 uppercase" data-testid="mfr-count">
            {mfrs.length === 0 ? "No manufacturers listed yet" : `${mfrs.length} brand${mfrs.length === 1 ? "" : "s"} available`}
          </div>
          <Link to={`/request-quote?product=${encodeURIComponent(data.name)}`} className="btn-primary flex items-center gap-2" data-testid="quote-all-btn">
            Request Quote for {data.name} <ArrowRight size={16}/>
          </Link>
        </div>

        {mfrs.length === 0 ? (
          <RecruitCard categoryName={data.name}/>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mfrs.map((m) => (
              <ManufacturerCard key={m.manufacturer_id} mfr={m} categoryName={data.name} onQuote={() => navigate(`/request-quote?product=${encodeURIComponent(data.name)}&manufacturer=${encodeURIComponent(m.manufacturer_id)}`)}/>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ManufacturerCard({ mfr, categoryName, onQuote }) {
  return (
    <div className="brutal-card-flat p-6 flex flex-col h-full" data-testid={`mfr-card-${mfr.manufacturer_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 bg-[#1A365D] text-white flex items-center justify-center flex-shrink-0">
          <Factory size={20} strokeWidth={1.8}/>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-xl font-bold uppercase text-[#1A365D] leading-tight truncate">{mfr.manufacturer_name}</h3>
          <div className="font-mono-ll text-[10px] tracking-widest text-[#C62828] mt-1">{mfr.products.length} PRODUCT{mfr.products.length === 1 ? "" : "S"}</div>
        </div>
      </div>

      <div className="mt-4 space-y-2 flex-1">
        {mfr.products.slice(0, 4).map((p) => (
          <div key={p.id} className="border-l-2 border-[#C62828] pl-3">
            <div className="flex items-start gap-2">
              <Package size={12} className="text-slate-400 mt-1 flex-shrink-0"/>
              <div className="flex-1">
                <div className="font-semibold text-sm text-[#1A365D]">{p.name}</div>
                <div className="text-xs text-slate-600 line-clamp-2">{p.description}</div>
              </div>
            </div>
          </div>
        ))}
        {mfr.products.length > 4 && (
          <div className="text-xs font-mono-ll tracking-widest text-slate-500 uppercase pl-5">+ {mfr.products.length - 4} more</div>
        )}
      </div>

      <button onClick={onQuote} className="btn-primary w-full mt-5 text-sm" data-testid={`quote-mfr-${mfr.manufacturer_id}`}>
        Request Quote from {mfr.manufacturer_name.split(" ")[0]}
      </button>
    </div>
  );
}

function RecruitCard({ categoryName }) {
  return (
    <div className="brutal-card p-10 text-center" data-testid="empty-state-recruit">
      <Factory className="mx-auto text-[#C62828]" size={48} strokeWidth={1.5}/>
      <h2 className="mt-4 font-heading text-3xl md:text-4xl font-black uppercase text-[#1A365D] leading-tight">
        Be the first {categoryName} brand on LumberLoop USA.
      </h2>
      <p className="mt-4 text-slate-700 max-w-xl mx-auto">
        Contractors looking for <strong>{categoryName}</strong> will find your brand here — directly, with no marketing middleman. List your products and dealer network to claim this category.
      </p>
      <div className="mt-8 flex gap-3 justify-center flex-wrap">
        <Link to="/register" className="btn-primary" data-testid="recruit-signup-btn">Sign Up Free</Link>
        <Link to="/manufacturers" className="btn-secondary" data-testid="recruit-learn-btn">Learn More</Link>
      </div>
    </div>
  );
}
